// packages/web/src/components/teach/HomeworkCard.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHomeworkStore } from '../../stores/homeworkStore';
import { useBasePath } from '../../contexts/BasePathContext';
import { Calendar, Users, BookOpen, Eye, Trash2 } from 'lucide-react';
import { Homework } from '../../services/homeworkApi';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Toast } from '../common/Toast';
import { useToast } from '../../hooks/useToast';

interface HomeworkCardProps {
  homework: Homework;
}

export const HomeworkCard: React.FC<HomeworkCardProps> = ({ homework }) => {
  const navigate = useNavigate();
  const { basePath } = useBasePath();
  const { deleteHomework } = useHomeworkStore();
  const { toast, success, error, hideToast } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      math: '数学',
      physics: '物理',
      chemistry: '化学',
      others: '其他',
    };
    return labels[category] || category;
  };

  const isOverdue = new Date(homework.due_date) < new Date();
  const completionRate = homework.total_students > 0
    ? Math.round((homework.completed_students / homework.total_students) * 100)
    : 0;

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteHomework(homework.id);
      success('删除成功');
      setShowDeleteConfirm(false);
    } catch (err: any) {
      error(err.response?.data?.error || '删除失败');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
      onClick={() => navigate(`${basePath}/homeworks/${homework.id}`)}
    >
      {/* 头部 */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-900 flex-1 line-clamp-2">
            {homework.name}
          </h3>
          <span className={`
            ml-2 px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap
            ${isOverdue ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}
          `}>
            {isOverdue ? '已截止' : '进行中'}
          </span>
        </div>

        {homework.description && (
          <p className="text-sm text-gray-600 line-clamp-2">{homework.description}</p>
        )}
      </div>

      {/* 信息 */}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <BookOpen className="w-4 h-4" />
          <span>{getCategoryLabel(homework.subject)}</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>截止: {new Date(homework.due_date).toLocaleDateString('zh-CN')}</span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="w-4 h-4" />
          <span>
            {homework.total_students} 名学生
            {homework.class_count > 0 && ` · ${homework.class_count} 个班级`}
          </span>
        </div>

        {/* 完成进度 */}
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-gray-600">完成进度</span>
            <span className="font-medium text-gray-900">{completionRate}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                completionRate === 100 ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${completionRate}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
            <span>{homework.completed_students} 已完成</span>
            <span>{homework.total_students - homework.completed_students} 未完成</span>
          </div>
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center gap-3">
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${basePath}/homeworks/${homework.id}`);
          }}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
        >
          <Eye className="w-4 h-4" />
          查看详情
        </button>

        {homework.total_students === 0 && (
          <button
            onClick={handleDelete}
            className="px-3 py-2 text-sm text-red-600 bg-red-50 rounded hover:bg-red-100 transition-colors"
            title="删除"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* 删除确认对话框 */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="确认删除"
        message={`确定要删除作业"${homework.name}"吗？删除后无法恢复。`}
        confirmText="删除"
        cancelText="取消"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isLoading={deleting}
        type="danger"
      />

      {/* Toast提示 */}
      {toast.isOpen && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};
